import XHR from '../utils/xhr';
import { dispatchAction } from '../dispatcher/AppDispatcher';
import ConfigActionTypes from '../constants/actionTypes/Config';
import renderModes from '../constants/renderModes';
import ConfigStore from '../stores/Config';

function requestConfig() {
  return {
    type: ConfigActionTypes.REQUEST_CONFIG
  };
}

function receiveConfig(config = {}) {
  return {
    type: ConfigActionTypes.RECEIVE_CONFIG,
    data: config
  };
}

function errorReceiveConfig(error) {
  return {
    type: ConfigActionTypes.ERROR_RECEIVE_CONFIG,
    data: error
  };
}

function requestProvisionAccount() {
  return {
    type: ConfigActionTypes.REQUEST_PROVISION_ACCOUNT
  };
}

function successProvisionAccount() {
  return {
    type: ConfigActionTypes.SUCCESS_PROVISION_ACCOUNT
  };
}

function errorProvisionAccount(error) {
  return {
    type: ConfigActionTypes.ERROR_PROVISION_ACCOUNT,
    data: error
  };
}

// Need to update renderMode so that provisionOlaAccount will know when to provision
export function updateRenderMode(renderMode) {
  dispatchAction({
    type: ConfigActionTypes.UPDATE_RENDER_MODE,
    data: renderMode
  });
}

// Need to update websiteId in the case that websiteId starts off as null
export function updateWebsiteId(websiteId) {
  dispatchAction({
    type: ConfigActionTypes.UPDATE_WEBSITE_ID,
    data: websiteId
  });
}

export function setClsHandlerRendered(data) {
  dispatchAction({
    type: ConfigActionTypes.SET_CLS_HANDLER_RENDERED,
    data
  });
}

function provisionOlaAccount() {
  const { websiteId, provisionInProgress, renderMode } = ConfigStore.getState();

  // Don't try to provision twice or provision in the ADD section.
  if (provisionInProgress || renderMode === renderModes.ADD) return;

  dispatchAction(requestProvisionAccount());

  return XHR.postRequest(`${ConfigStore.getProvisioningBaseUrl()}`, {
    data: { account_uid: websiteId }
  })
    .then(() => {
      dispatchAction(successProvisionAccount());
      return fetchConfig();
    })
    .catch(() => {
      // TODO: Get actual error to pass as argument
      dispatchAction(errorProvisionAccount('error'));
    });
}

export function setConfig(config) {
  const { configWasSet, websiteId } = ConfigStore.getState();

  // Don't set config more than once for same websiteId
  if (configWasSet && websiteId === config.websiteId) return;

  dispatchAction({
    type: ConfigActionTypes.SET_CONFIG,
    data: config
  });
}

const MAX_RETRIES = 4;
const INITIAL_RETRY_DELAY = 2000; // 2 seconds
const JITTER_MS = 1000;

async function wait(timeInMilliseconds) {
  return new Promise(resolve => setTimeout(resolve, timeInMilliseconds));
}

async function fetchConfigWithRetry(retryCount = 0) {
  try {
    const res = await XHR.getRequest(`${ConfigStore.getApiBaseUrl()}/config`);
    const { config } = res;
    dispatchAction(receiveConfig(config));
    return { isSuccess: true };
  } catch (err) {
    if (err.error && err.error.status_code === 404) {
      // If we haven't provisioned yet, do that first
      if (retryCount === 0) {
        await provisionOlaAccount();
        // After provisioning, try fetching config again
        return fetchConfigWithRetry(1);
      }
    }

    // If we still have retries left and it's not a 404, retry with exponential backoff
    if (retryCount < MAX_RETRIES) {
      const baseDelay = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
      const jitter = Math.floor(Math.random() * JITTER_MS);
      const delay = baseDelay + jitter;
      console.warn(
        `Config fetch failed, retrying in ${delay}ms... (attempt ${retryCount + 1}/${MAX_RETRIES})`
      );
      await wait(delay);
      return fetchConfigWithRetry(retryCount + 1);
    }

    // If we've exhausted all retries, dispatch error
    dispatchAction(errorReceiveConfig(err));
    return { isSuccess: false };
  }
}

export function fetchConfig() {
  const configState = ConfigStore.getState();
  const { isConfigLoaded, isFetching, websiteId } = configState;

  // If the config was already loaded, don't load it again to prevent more calls to the API.
  if (isConfigLoaded) {
    return Promise.resolve({ isSuccess: true });
  }

  if (isFetching || !websiteId) {
    return Promise.resolve({ isSuccess: false });
  }

  dispatchAction(requestConfig());
  return fetchConfigWithRetry();
}

export function reportWidgetAdded() {
  return XHR.postRequest(`${ConfigStore.getApiBaseUrl()}/widgets`);
}

function requestCountryStates() {
  return {
    type: ConfigActionTypes.REQUEST_COUNTRY_STATES
  };
}

function receiveCountryStates(data) {
  return {
    type: ConfigActionTypes.RECEIVE_COUNTRY_STATES,
    data
  };
}

function errorReceiveCountryStates(error) {
  return {
    type: ConfigActionTypes.ERROR_RECEIVE_COUNTRY_STATES,
    data: error
  };
}

export function fetchCountryStates() {
  const {
    areCountryStatesLoaded,
    isFetchingStates,
    business_country: { id: countryId }
  } = ConfigStore.getState();

  if (areCountryStatesLoaded || isFetchingStates) {
    return;
  }

  dispatchAction(requestCountryStates());
  return XHR.getRequest(`${ConfigStore.getApiBaseUrl()}/countries/${countryId}/states`)
    .then(res => {
      dispatchAction(receiveCountryStates(res.sort((a, b) => a.name.localeCompare(b.name))));
    })
    .catch(err => {
      dispatchAction(errorReceiveCountryStates(err));
    });
}

function setHivemindConfig(config = {}) {
  return {
    type: ConfigActionTypes.SET_HIVEMIND_EXPERIMENTS,
    data: config
  };
}

export const fetchHiveMindExperimentsConfig = () => {
  const url = `${ConfigStore.getApiBaseUrl()}/hivemind`;

  return XHR.getRequest(url, {
    params: {
      useReferrer: false
    }
  })
    .then(res => {
      dispatchAction(setHivemindConfig(res.cohorts));
    })
    .catch(error => {
      console.error('An error occurred: action', error);
    });
};
